import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import { PermissionEnum } from '@ui/src';
import { v4 as uuid } from 'uuid';
import {
  AccountsCurrencies,
  AccountsCurrenciesFormValues,
  AccountsFlows,
  AccountsFlowsFormValues,
  DictionaryItem,
  ExpectedFundsOrigins,
  ExpectedFundsOriginsFormValues,
  NewApplicationCorporateInfo,
} from '../../types';
import { CorporateQuestionnarieFormValues } from '../../../corporate/types';
import {
  useCreateAccountsCurrencyMutation,
  useCreateAccountsFlowMutation,
  useCreateExpectedFundsOriginsMutation,
  useDeleteAccountsCurrencyMutation,
  useDeleteAccountsFlowMutation,
  useDeleteExptectedFundsOriginsMutation,
  useLazyGetAccountsCurrenciesQuery,
  useLazyGetAccountsFlowsQuery,
  useLazyGetAnnualIncomeDictionaryQuery,
  useLazyGetApplicationQuestionnarieQuery,
  useLazyGetBusinessActivitiesDictionaryQuery,
  useLazyGetCustomersMainClientsDictionaryQuery,
  useLazyGetCustomerSourceOfFundsQuery,
  useLazyGetCustomerSourceOfWealthQuery,
  useLazyGetExpectedFundsOriginsQuery,
  useLazyGetRelationShipToCustomerDictionaryQuery,
  useLazyGetSalesChannelsDictionaryQuery,
  useLazyGetSourceOfWealthForCorporateDictionaryQuery,
  useLazyGetTypeOfAccountsDictionaryQuery,
  useUpdateAccountsCurrencyMutation,
  useUpdateAccountsFlowMutation,
  useUpdateExpectedFundsOriginsMutation,
} from '../../store/services';

const useQuestionnarie = ({
  expectedFundsOrigins,
  questionnarieId,
  accountsCurrencies,
  accountsFlows,
  newApplicationInfo,
  questionnarie,
  activePermissions,
  relationshipToTheCustomerDictionary,
  sourceOfWealth,
  busniessActivities,
  customerMainCleints,
  salesChannels,
  customerSourceOfWealth,
  customerSourceOfFunds,
  annualIncomes,
  typeOfAccounts,
}: {
  expectedFundsOrigins?: ExpectedFundsOrigins[];
  questionnarieId?: string;
  accountsCurrencies?: AccountsCurrencies[];
  accountsFlows?: AccountsFlows[];
  newApplicationInfo?: NewApplicationCorporateInfo;
  questionnarie?: CorporateQuestionnarieFormValues;
  activePermissions?: PermissionEnum[];
  relationshipToTheCustomerDictionary?: DictionaryItem[];
  sourceOfWealth?: DictionaryItem[];
  busniessActivities?: DictionaryItem[];
  customerMainCleints?: DictionaryItem[];
  salesChannels?: DictionaryItem[];
  customerSourceOfWealth?: DictionaryItem[];
  customerSourceOfFunds?: DictionaryItem[];
  annualIncomes?: DictionaryItem[];
  typeOfAccounts?: DictionaryItem[];
}) => {
  const [defaultQuestionnarieFormValues, setDefaultQuestionnarieFormValues] =
    useState({});
  const [totalIncomeError, setTotalIncomeError] = useState('');
  const [accountsCurrenciesValues, setAccountsCurrenciesValues] = useState<
    AccountsCurrenciesFormValues[]
  >([
    {
      id: 'accounts-currency-1',
      accountOpeningPurpose: '',
      currencyId: '',
      plannedTransactionNature: '',
    },
  ]);
  const [selectedAccountsCurrencyId, setSelectedAccountsCurrencyId] =
    useState('');
  const [accountsFlowsValues, setAccountsFlowsValues] = useState<
    AccountsFlowsFormValues[]
  >([]);
  const [expectedFundsOriginsValues, setExpectedFundsOriginsValues] = useState<
    ExpectedFundsOriginsFormValues[]
  >([
    {
      id: 'expected-funds-origins-1',
      currencyId: '',
      mainCounterpartiesAndWebsites: '',
      countryOfIncorporationId: '',
      counterpartyBankCountryIds: [],
      activityCountryIds: [],
      mainActivities: '',
      transactionReason: '',
      transactionType: 'INCOMING',
    },
    {
      id: 'expected-funds-origins-2',
      currencyId: '',
      mainCounterpartiesAndWebsites: '',
      countryOfIncorporationId: '',
      counterpartyBankCountryIds: [],
      activityCountryIds: [],
      mainActivities: '',
      transactionReason: '',
      transactionType: 'OUTGOING',
    },
  ]);
  const [selectedExpectedFundsOriginsId, setSelectedFundsOriginsId] =
    useState('');

  const {
    control: questionnarieControl,
    reset: questionnarieReset,
    clearErrors: questionnarieClearErrors,
    formState: { errors: questionnarieErrors, isDirty: questionnarieIsDirty },
    watch: questionnarieWatch,
    getValues: questionnarieGetValues,
    setError: questionnarieSetError,
    setValue: questionnarieSetValue,
  } = useForm<CorporateQuestionnarieFormValues>();

  const questionnarieFields = questionnarieWatch();

  const [
    getApplicationQuestionnarie,
    { isLoading: questionnarieIsLoading, isFetching: questionnarieIsFetching },
  ] = useLazyGetApplicationQuestionnarieQuery();
  const [getRelationshipDictionary] =
    useLazyGetRelationShipToCustomerDictionaryQuery();
  const [getSourceOfWealthForCorporateDictionary] =
    useLazyGetSourceOfWealthForCorporateDictionaryQuery();
  const [getBusinessActivitiesDictionary] =
    useLazyGetBusinessActivitiesDictionaryQuery();
  const [getCustomersMainClientsDictionary] =
    useLazyGetCustomersMainClientsDictionaryQuery();
  const [getSalesChannels] = useLazyGetSalesChannelsDictionaryQuery();
  const [getCustomerSourceOfWealth] = useLazyGetCustomerSourceOfWealthQuery();
  const [getCustomerSourceOfFunds] = useLazyGetCustomerSourceOfFundsQuery();
  const [getAnnualIncomeDictionary] = useLazyGetAnnualIncomeDictionaryQuery();
  const [getTypeOfAccountsDictionary] =
    useLazyGetTypeOfAccountsDictionaryQuery();
  const [
    getAccountsCurrencies,
    {
      isLoading: accountsCurrenciesIsLoading,
      isFetching: accountsCurrenciesIsFetching,
    },
  ] = useLazyGetAccountsCurrenciesQuery();
  const [
    createAccountsCurrencies,
    { isLoading: createAccountsCurrenciesIsProgress },
  ] = useCreateAccountsCurrencyMutation();
  const [
    updateAccountsCurrencies,
    { isLoading: updateAccountsCurrenciesInProgress },
  ] = useUpdateAccountsCurrencyMutation();
  const [
    deleteAccountsCurrencies,
    {
      isLoading: deleteAccountsCurrenciesInProgress,
      isSuccess: deleteAccountsCurrenciesIsSuccess,
    },
  ] = useDeleteAccountsCurrencyMutation();
  const [
    getAccountsFlows,
    { isLoading: accountsFlowsIsLoading, isFetching: accountsFlowsIsFetching },
  ] = useLazyGetAccountsFlowsQuery();
  const [createAccountsFlows, { isLoading: createAccountsFlowsIsProgress }] =
    useCreateAccountsFlowMutation();
  const [updateAccountsFlows, { isLoading: updateAccountsFlowsInProgress }] =
    useUpdateAccountsFlowMutation();
  const [deleteAccountsFlows] = useDeleteAccountsFlowMutation();
  const [
    getExpectedFundsOrigins,
    {
      isLoading: expectedFundsOriginsIsLoading,
      isFetching: expectedFundsOriginsIsFetching,
    },
  ] = useLazyGetExpectedFundsOriginsQuery();
  const [
    createExpectedFundsOrigins,
    { isLoading: createExpectedFundsOriginsInProgress },
  ] = useCreateExpectedFundsOriginsMutation();
  const [
    updateExpectedFundsOrigins,
    { isLoading: updateExpectedFundsOriginsInProgress },
  ] = useUpdateExpectedFundsOriginsMutation();
  const [
    deleteExpectedFundsOrigins,
    {
      isLoading: deleteExpectedFundsOriginsInProgress,
      isSuccess: deleteExpectedFundsOriginsIsSuccess,
    },
  ] = useDeleteExptectedFundsOriginsMutation();

  const onSaveExpectedFundsOrigins = async () => {
    const expectedFundsOriginsForCreation: ExpectedFundsOriginsFormValues[] =
      [];
    const expectedFundsOriginsForUpdate: ExpectedFundsOriginsFormValues[] = [];

    expectedFundsOriginsValues?.forEach((item) => {
      if (item.id?.includes('expected-funds-origins')) {
        expectedFundsOriginsForCreation.push(item);
      } else {
        const currentExpectedFundsOrigins = expectedFundsOrigins?.find(
          (value) => value.id === item.id,
        );

        if (currentExpectedFundsOrigins) {
          if (
            !isEqual(item, {
              id: currentExpectedFundsOrigins.id,
              currencyId: currentExpectedFundsOrigins.currencyId,
              mainCounterpartiesAndWebsites:
                currentExpectedFundsOrigins.mainCounterpartiesAndWebsites,
              countryOfIncorporationId:
                currentExpectedFundsOrigins.countryOfIncorporationId,
              counterpartyBankCountryIds:
                currentExpectedFundsOrigins.counterpartyBankCountryIds,
              activityCountryIds:
                currentExpectedFundsOrigins.activityCountryIds,
              mainActivities: currentExpectedFundsOrigins.mainActivities,
              transactionReason: currentExpectedFundsOrigins.transactionReason,
              transactionType: currentExpectedFundsOrigins.transactionType,
            })
          ) {
            expectedFundsOriginsForUpdate.push(item);
          }
        }
      }
    });

    if (expectedFundsOriginsForCreation?.length) {
      await Promise.all(
        expectedFundsOriginsForCreation
          ?.filter((item) => item.currencyId)
          ?.map(async (item) => {
            const response = await createExpectedFundsOrigins({
              id: questionnarieId as string,
              body: {
                currencyId: item.currencyId,
                mainCounterpartiesAndWebsites:
                  item.mainCounterpartiesAndWebsites || '',
                countryOfIncorporationId: item.countryOfIncorporationId || '',
                counterpartyBankCountryIds:
                  item.counterpartyBankCountryIds || '',
                activityCountryIds: item.activityCountryIds || '',
                mainActivities: item.mainActivities || '',
                transactionReason: item.transactionReason || '',
                transactionType: item.transactionType || '',
              },
            });

            return response;
          }),
      );
    }

    if (expectedFundsOriginsForUpdate?.length) {
      await Promise.all(
        expectedFundsOriginsForUpdate?.map(async (item) => {
          const response = await updateExpectedFundsOrigins({
            id: item.id as string,
            body: {
              currencyId: item.currencyId,
              mainCounterpartiesAndWebsites:
                item.mainCounterpartiesAndWebsites || '',
              countryOfIncorporationId: item.countryOfIncorporationId || '',
              counterpartyBankCountryIds: item.counterpartyBankCountryIds || '',
              activityCountryIds: item.activityCountryIds || '',
              mainActivities: item.mainActivities || '',
              transactionReason: item.transactionReason || '',
              transactionType: item.transactionType || '',
            },
          });

          return response;
        }),
      );
    }
  };

  const onSaveAccountsCurrencies = async () => {
    const accountsCurrenciesForCreation: AccountsCurrenciesFormValues[] = [];
    const accountsCurrenciesForUpdate: AccountsCurrenciesFormValues[] = [];

    accountsCurrenciesValues?.forEach((item) => {
      if (item.id?.includes('accounts-currency')) {
        accountsCurrenciesForCreation.push(item);
      } else {
        const currentAccountsCurrency = accountsCurrencies?.find(
          (currency) => currency.id === item.id,
        );

        if (currentAccountsCurrency) {
          if (
            !isEqual(item, {
              id: currentAccountsCurrency.id,
              currencyId: currentAccountsCurrency.currencyId,
              accountOpeningPurpose:
                currentAccountsCurrency.accountOpeningPurpose,
              plannedTransactionNature:
                currentAccountsCurrency.plannedTransactionNature,
            })
          ) {
            accountsCurrenciesForUpdate.push(item);
          }
        }
      }
    });

    if (accountsCurrenciesForCreation?.length) {
      await Promise.all(
        accountsCurrenciesForCreation
          ?.filter((item) => item.currencyId)
          ?.map(async (item) => {
            const response = await createAccountsCurrencies({
              id: questionnarieId as string,
              body: {
                currencyId: item.currencyId,
                accountOpeningPurpose: item.accountOpeningPurpose || '',
                plannedTransactionNature: item.plannedTransactionNature || '',
              },
            });

            return response;
          }),
      );
    }

    if (accountsCurrenciesForUpdate?.length) {
      await Promise.all(
        accountsCurrenciesForUpdate?.map(async (item) => {
          const response = await updateAccountsCurrencies({
            id: item.id as string,
            body: {
              currencyId: item.currencyId,
              accountOpeningPurpose: item.accountOpeningPurpose || '',
              plannedTransactionNature: item.plannedTransactionNature || '',
            },
          });

          return response;
        }),
      );
    }
  };

  const onSaveAccountsFlows = async () => {
    const accountsFlowsForCreation: AccountsFlowsFormValues[] = [];
    const accountsFlowsForUpdate: AccountsFlowsFormValues[] = [];

    accountsFlowsValues?.forEach((item) => {
      if (item.id?.includes('accounts-flow')) {
        accountsFlowsForCreation.push(item);
      } else {
        const currentAccountsFlow = accountsFlows?.find(
          (flow) => flow.id === item.id,
        );

        if (currentAccountsFlow) {
          if (
            !isEqual(item, {
              id: currentAccountsFlow.id,
              transactionType: currentAccountsFlow.transactionType,
              currencyId: currentAccountsFlow.currencyId,
              expectedAnnualCreditTurnover:
                currentAccountsFlow.expectedAnnualCreditTurnover.toString(),
              maxTransferAmountPerTransaction:
                currentAccountsFlow.maxTransferAmountPerTransaction.toString(),
              transactionFrequency:
                currentAccountsFlow.transactionFrequency.toString(),
              transactionFrequencyPeriodMonths:
                currentAccountsFlow.transactionFrequencyPeriodMonths.toString(),
            })
          ) {
            accountsFlowsForUpdate.push(item);
          }
        }
      }
    });

    if (
      accountsFlowsForCreation?.filter(
        (item) =>
          item.expectedAnnualCreditTurnover &&
          item.maxTransferAmountPerTransaction &&
          item.transactionFrequency &&
          item.transactionFrequencyPeriodMonths,
      )?.length
    ) {
      await Promise.all(
        accountsFlowsForCreation
          ?.filter(
            (item) =>
              item.expectedAnnualCreditTurnover &&
              item.maxTransferAmountPerTransaction &&
              item.transactionFrequency &&
              item.transactionFrequencyPeriodMonths,
          )
          ?.map(async (item) => {
            const response = await createAccountsFlows({
              id: questionnarieId as string,
              body: {
                transactionType: item.transactionType,
                currencyId: item.currencyId,
                expectedAnnualCreditTurnover: Number(
                  item.expectedAnnualCreditTurnover,
                ),
                maxTransferAmountPerTransaction: Number(
                  item.maxTransferAmountPerTransaction,
                ),
                transactionFrequency: Number(item.transactionFrequency),
                transactionFrequencyPeriodMonths: Number(
                  item.transactionFrequencyPeriodMonths,
                ),
              },
            });

            return response;
          }),
      );
    }

    if (accountsFlowsForUpdate?.length) {
      await Promise.all(
        accountsFlowsForUpdate?.map(async (item) => {
          const response = await updateAccountsFlows({
            id: item.id as string,
            body: {
              transactionType: item.transactionType,
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover: Number(
                item.expectedAnnualCreditTurnover,
              ),
              maxTransferAmountPerTransaction: Number(
                item.maxTransferAmountPerTransaction,
              ),
              transactionFrequency: Number(item.transactionFrequency),
              transactionFrequencyPeriodMonths: Number(
                item.transactionFrequencyPeriodMonths,
              ),
            },
          });

          return response;
        }),
      );
    }
  };

  const onDeleteAccountCurrencies = async (id: string) => {
    if (id.includes('accounts-currency')) {
      setAccountsCurrenciesValues(
        accountsCurrenciesValues?.filter((item) => item.id !== id),
      );

      const currentAccountsCurrency = accountsCurrenciesValues?.find(
        (item) => item.id === id,
      );

      if (currentAccountsCurrency) {
        const currentAccountsFlows = accountsFlowsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        const currentExpectedFundsOrigins = expectedFundsOriginsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        if (currentAccountsFlows?.length) {
          setAccountsFlowsValues(
            accountsFlowsValues?.filter((item) =>
              currentAccountsFlows.every((flow) => item.id !== flow.id),
            ),
          );
        }

        if (currentExpectedFundsOrigins?.length) {
          setExpectedFundsOriginsValues(
            expectedFundsOriginsValues?.filter((item) =>
              currentExpectedFundsOrigins.every(
                (expectedFund) => item.id !== expectedFund.id,
              ),
            ),
          );
        }
      }
    } else {
      setSelectedAccountsCurrencyId(id);

      const currentAccountsCurrency = accountsCurrenciesValues?.find(
        (item) => item.id === id,
      );

      if (currentAccountsCurrency) {
        const currentAccountsFlows = accountsFlowsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        const currentExpectedFundsOrigins = expectedFundsOriginsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        if (currentAccountsFlows?.length) {
          await Promise.all(
            currentAccountsFlows
              ?.filter((item) => !item.id?.includes('accounts-flow'))
              ?.map(async (item) => {
                const response = await deleteAccountsFlows(item.id as string);

                return response;
              }),
          );

          if (
            currentAccountsFlows?.filter((item) =>
              item.id?.includes('accounts-flow'),
            ).length
          ) {
            const notCreatedAccountsFlow = currentAccountsFlows?.filter(
              (item) => item.id?.includes('accounts-flow'),
            );

            setAccountsFlowsValues(
              accountsFlowsValues?.filter((item) =>
                notCreatedAccountsFlow.every((flow) => item.id !== flow.id),
              ),
            );
          }
        }

        if (currentExpectedFundsOrigins?.length) {
          await Promise.all(
            currentExpectedFundsOrigins
              ?.filter((item) => !item.id?.includes('expected-funds-origins'))
              ?.map(async (item) => {
                const response = await deleteExpectedFundsOrigins(
                  item.id as string,
                );

                return response;
              }),
          );

          if (
            currentExpectedFundsOrigins?.filter((item) =>
              item.id?.includes('expected-funds-origins'),
            )?.length
          ) {
            const notCreatedExpectedFundsOrigins =
              currentExpectedFundsOrigins?.filter((item) =>
                item.id?.includes('expected-funds-origins'),
              );

            setExpectedFundsOriginsValues(
              expectedFundsOriginsValues?.filter((item) =>
                notCreatedExpectedFundsOrigins.every(
                  (expectedFundsOrigin) => item.id !== expectedFundsOrigin.id,
                ),
              ),
            );
          }
        }
      }

      await deleteAccountsCurrencies(id);
    }
  };

  const onDeleteExpectedFundsOrigins = async (id: string) => {
    if (id.includes('expected-funds-origins')) {
      setExpectedFundsOriginsValues(
        expectedFundsOriginsValues?.filter((item) => item.id !== id),
      );
    } else {
      setSelectedFundsOriginsId(id);

      await deleteExpectedFundsOrigins(id);
    }
  };

  useEffect(() => {
    if (accountsCurrencies) {
      setAccountsCurrenciesValues(
        accountsCurrencies?.map((item) => ({
          id: item.id,
          currencyId: item.currencyId,
          accountOpeningPurpose: item.accountOpeningPurpose,
          plannedTransactionNature: item.plannedTransactionNature,
        })),
      );
    }
  }, [accountsCurrencies]);

  useEffect(() => {
    if (accountsFlows && accountsCurrencies) {
      const nextAccountsFlows = accountsFlows?.map((item) => ({
        id: item.id,
        transactionType: item.transactionType,
        currencyId: item.currencyId,
        expectedAnnualCreditTurnover:
          item.expectedAnnualCreditTurnover.toString(),
        maxTransferAmountPerTransaction:
          item.maxTransferAmountPerTransaction.toString(),
        transactionFrequency: item.transactionFrequency.toString(),
        transactionFrequencyPeriodMonths:
          item.transactionFrequencyPeriodMonths.toString(),
      }));

      const notCreatedCurrencies: AccountsCurrencies[] = [];

      accountsCurrencies?.forEach((item) => {
        if (
          !nextAccountsFlows?.find(
            (flow) => flow.currencyId === item.currencyId,
          )
        ) {
          notCreatedCurrencies.push(item);
        }
      });

      if (notCreatedCurrencies?.length) {
        notCreatedCurrencies?.forEach((item) => {
          nextAccountsFlows.push(
            {
              id: `accounts-flow-${uuid()}`,
              transactionType: 'INCOMING',
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover: '',
              maxTransferAmountPerTransaction: '',
              transactionFrequency: '',
              transactionFrequencyPeriodMonths: '',
            },
            {
              id: `accounts-flow-${uuid()}`,
              transactionType: 'OUTGOING',
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover: '',
              maxTransferAmountPerTransaction: '',
              transactionFrequency: '',
              transactionFrequencyPeriodMonths: '',
            },
          );
        });
      }

      setAccountsFlowsValues(nextAccountsFlows);
    }
  }, [accountsFlows, accountsCurrencies]);

  useEffect(() => {
    if (expectedFundsOrigins) {
      setExpectedFundsOriginsValues(
        expectedFundsOrigins?.map((item) => ({
          id: item.id,
          currencyId: item.currencyId,
          mainCounterpartiesAndWebsites: item.mainCounterpartiesAndWebsites,
          countryOfIncorporationId: item.countryOfIncorporationId,
          counterpartyBankCountryIds: item.counterpartyBankCountryIds,
          activityCountryIds: item.activityCountryIds,
          mainActivities: item.mainActivities,
          transactionReason: item.transactionReason,
          transactionType: item.transactionType,
        })),
      );
    }
  }, [expectedFundsOrigins]);

  useEffect(() => {
    if (deleteAccountsCurrenciesIsSuccess) {
      setSelectedAccountsCurrencyId('');
    }
  }, [deleteAccountsCurrenciesIsSuccess]);

  useEffect(() => {
    if (deleteExpectedFundsOriginsIsSuccess) {
      setSelectedFundsOriginsId('');
    }
  }, [deleteExpectedFundsOriginsIsSuccess]);

  useEffect(() => {
    if (newApplicationInfo && newApplicationInfo?.applicationScheme) {
      if (
        !questionnarie &&
        (newApplicationInfo?.applicationScheme?.accountantDetailsActive ||
          newApplicationInfo?.applicationScheme
            ?.auditedByExternalComplianceActive ||
          newApplicationInfo?.applicationScheme
            ?.changesInManagementOrOwnershipActive ||
          newApplicationInfo?.applicationScheme
            ?.complianceConsultantDetailsActive ||
          newApplicationInfo?.applicationScheme?.fcaNumberActive ||
          newApplicationInfo?.applicationScheme
            ?.hmrcMlrNumberAndExpiryDateActive ||
          newApplicationInfo?.applicationScheme?.icoNumberActive ||
          newApplicationInfo?.applicationScheme?.itProviderDetailsActive ||
          newApplicationInfo?.applicationScheme?.mlroDetailsActive ||
          newApplicationInfo?.applicationScheme?.numberOfAgentsActive ||
          newApplicationInfo?.applicationScheme?.numberOfBranchesActive ||
          newApplicationInfo?.applicationScheme
            ?.onboardCorporateClientsActive ||
          newApplicationInfo?.applicationScheme?.onboardMsbActive ||
          newApplicationInfo?.applicationScheme?.paymentsForCharitiesActive ||
          newApplicationInfo?.applicationScheme?.payoutPartnerDetailsActive ||
          newApplicationInfo?.applicationScheme
            ?.regulatoryInvestigationActive ||
          newApplicationInfo?.applicationScheme?.screeningActive ||
          newApplicationInfo?.applicationScheme?.transactionMonitoringActive ||
          newApplicationInfo?.applicationScheme?.typeOfBusinessLicenceActive ||
          newApplicationInfo?.applicationScheme?.visitedByAuthoritiesActive ||
          newApplicationInfo?.applicationScheme
            ?.workingWithAnyOtherIPSPActive ||
          newApplicationInfo?.hasQuestionnaire)
      ) {
        getApplicationQuestionnarie(newApplicationInfo?.id);
      }
    }
  }, [newApplicationInfo, questionnarie]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.DICTIONARIES_ITEMS_LIST) &&
      newApplicationInfo &&
      Boolean(
        newApplicationInfo?.applicationScheme?.id ===
        'cdbb8c71-90e6-4a5e-964f-e55169e0c4df',
      )
    ) {
      if (!relationshipToTheCustomerDictionary) {
        getRelationshipDictionary();
      }

      if (!sourceOfWealth) {
        getSourceOfWealthForCorporateDictionary();
      }

      if (!busniessActivities) {
        getBusinessActivitiesDictionary();
      }

      if (!customerMainCleints) {
        getCustomersMainClientsDictionary();
      }

      if (!salesChannels) {
        getSalesChannels();
      }

      if (!customerSourceOfWealth) {
        getCustomerSourceOfWealth();
      }

      if (!customerSourceOfFunds) {
        getCustomerSourceOfFunds();
      }

      if (!annualIncomes) {
        getAnnualIncomeDictionary();
      }

      if (!typeOfAccounts) {
        getTypeOfAccountsDictionary();
      }
    }
  }, [
    activePermissions,
    newApplicationInfo,
    relationshipToTheCustomerDictionary,
    sourceOfWealth,
    busniessActivities,
    customerMainCleints,
    salesChannels,
    customerSourceOfWealth,
    customerSourceOfFunds,
    annualIncomes,
    typeOfAccounts,
  ]);

  useEffect(() => {
    if (questionnarie) {
      questionnarieReset(questionnarie);
      setDefaultQuestionnarieFormValues(questionnarie);
    }
  }, [questionnarie]);

  useEffect(() => {
    if (
      newApplicationInfo &&
      questionnarieId &&
      Boolean(
        newApplicationInfo?.applicationScheme?.id ===
        'cdbb8c71-90e6-4a5e-964f-e55169e0c4df',
      )
    ) {
      if (!accountsCurrencies) {
        getAccountsCurrencies(questionnarieId);
      }

      if (!accountsFlows) {
        getAccountsFlows(questionnarieId);
      }

      if (!expectedFundsOrigins) {
        getExpectedFundsOrigins(questionnarieId);
      }
    }
  }, [questionnarieId, newApplicationInfo, accountsCurrencies, accountsFlows]);

  return {
    questionnarieFields,
    accountsCurrenciesValues,
    expectedFundsOriginsValues,
    accountsFlowsValues,
    questionnarieGetValues,
    questionnarieSetError,
    onSaveAccountsCurrencies,
    onSaveAccountsFlows,
    onSaveExpectedFundsOrigins,
    defaultQuestionnarieFormValues,
    questionnarieIsLoading,
    questionnarieIsFetching,
    createAccountsCurrenciesIsProgress,
    createAccountsFlowsIsProgress,
    createExpectedFundsOriginsInProgress,
    updateAccountsCurrenciesInProgress,
    updateAccountsFlowsInProgress,
    updateExpectedFundsOriginsInProgress,
    questionnarieClearErrors,
    totalIncomeError,
    questionnarieControl,
    questionnarieErrors,
    questionnarieSetValue,
    setTotalIncomeError,
    setAccountsCurrenciesValues,
    onDeleteAccountCurrencies,
    selectedAccountsCurrencyId,
    deleteAccountsCurrenciesInProgress,
    setAccountsFlowsValues,
    selectedExpectedFundsOriginsId,
    accountsCurrenciesIsLoading,
    accountsCurrenciesIsFetching,
    accountsFlowsIsLoading,
    accountsFlowsIsFetching,
    expectedFundsOriginsIsLoading,
    expectedFundsOriginsIsFetching,
    deleteExpectedFundsOriginsInProgress,
    onDeleteExpectedFundsOrigins,
    setExpectedFundsOriginsValues,
    questionnarieIsDirty,
  };
};

export default useQuestionnarie;
